import { requestCreator, UnknownError } from "../../common/actions/VisitApi";

/**
 * GET /companies/:companyId
 */

export type Avatar = WtdModels.Partial<WtdModels.Image, "id" | "url">;
export type Background = WtdModels.Partial<WtdModels.Image, "id" | "url">;

// Sync with fields/include of requestToUpdateCompany query
export type Company = WtdModels.Partial<
  WtdModels.Company,
  | "id"
  | "name"
  | "company_name"
  | "company_path"
  | "font_color_code"
  | "short_location"
  | "address_prefix"
  | "address_suffix"
  | "latitude"
  | "longitude"
  | "url"
  | "founded_on"
  | "founder"
  | "origin"
  | "mission_statement"
  | "misc_tag_names"
  | "payroll_number"
  | "policy_update"
  | "is_following"
  | "project_count"
  | "post_count"
  | "lastest_post_published_at"
  | "has_descriptions"
  | "is_international_feed"
  | "country"
  | "twitter"
  | "facebook",
  {
    avatar: Avatar;
    background: Background;
  }
>;

// Sync with fields of requestToUpdateCompanyValues query
export type CompanyValue = WtdModels.Partial<
  WtdModels.CompanyValue,
  | "id"
  | "feature"
  | "feature_cd"
  | "feature_color"
  | "feature_title"
  | "feature_title_with_break"
  | "description"
  | "rank"
>;

export type CompanyMembershipSection = WtdModels.Partial<
  WtdModels.CompanyMembershipSection,
  "id" | "name" | "display_order" | "description" | "members_count",
  {
    cover_members: Array<
      WtdModels.Partial<
        WtdModels.User,
        "id" | "name",
        {
          avatar: WtdModels.Partial<WtdModels.Image, "id" | "url">;
        }
      >
    >;
  }
>;

export type FeaturedCompanymembership = WtdModels.Partial<
  WtdModels.CompanyMembership,
  "id" | "display_order" | "featured_display_order" | "position",
  {
    section: WtdModels.Partial<WtdModels.CompanyMembershipSection, "id">;
    user: WtdModels.Partial<
      WtdModels.User,
      "id" | "name" | "user_path",
      {
        avatar: WtdModels.Partial<WtdModels.Image, "id" | "url">;
        profile: WtdModels.Partial<
          WtdModels.Profile,
          "id" | "introduction" | "position",
          {
            cover_post: WtdModels.Partial<
              WtdModels.Post,
              "id" | "post_path" | "title",
              {
                image: WtdModels.Partial<WtdModels.Image, "id" | "url">;
              }
            >;
          }
        >;
      }
    >;
  }
>;

export type FeaturedProject = WtdModels.Partial<
  WtdModels.Project,
  "id" | "project_path" | "title" | "looking_for",
  {
    image: WtdModels.Partial<WtdModels.Image, "id" | "url">;
  }
>;

export type PortfolioProject = WtdModels.Partial<
  WtdModels.PortfolioProject,
  "id" | "canonical_url" | "title" | "description",
  {
    cover_image: WtdModels.Partial<WtdModels.Image, "id" | "url">;
  }
>;

export type CompanyPostPin = WtdModels.Partial<
  WtdModels.CompanyPostPin,
  "id" | "category_cd",
  {
    company: WtdModels.Partial<WtdModels.Company, "id">;
    post: WtdModels.Partial<
      WtdModels.Post,
      "id" | "post_path" | "title",
      {
        image: WtdModels.Partial<WtdModels.Image, "id" | "url">;
      }
    >;
  }
>;

export type PinnablePost = WtdModels.Partial<
  WtdModels.Post,
  "id" | "post_path" | "title",
  {
    image: WtdModels.Partial<WtdModels.Image, "id" | "url">;
  }
>;

export interface CompanyShowResponse {
  company: WtdModels.Union<
    Company,
    WtdModels.Partial<
      WtdModels.Company,
      "id",
      // TODO(stomk): Remove visible_company_values
      {
        visible_company_values: Array<WtdModels.Partial<WtdModels.CompanyValue, "id">>;
      }
    >
  >;
  featured_memberships: FeaturedCompanymembership[];
  company_membership_sections: CompanyMembershipSection[] | null;
  projects: FeaturedProject[];
  portfolio_projects: PortfolioProject[];
  company_values: CompanyValue[];
  post_pins: CompanyPostPin[];
  latest_pinnable_posts: PinnablePost[];
  company_followings: Array<WtdModels.Partial<WtdModels.CompanyFollowing, "id" | "company_id">>;
  experimentings: Array<WtdModels.Partial<WtdModels.Experimenting, "experiment_name" | "variation">>;
}

export const requestToLoadCompanyShow = requestCreator<number | string, CompanyShowResponse, Error>(
  companyIdOrName => ({
    path: `/companies/${companyIdOrName}.json`,
  })
);

/**
 * GET /companies/:companyId/about
 */

export type CompanyDescription = WtdModels.Partial<
  WtdModels.CompanyDescription,
  "type" | "description",
  {
    images: Array<WtdModels.Partial<WtdModels.DescriptionImage, "id" | "url" | "caption">>;
  }
>;

export interface CompanyAboutResponse {
  company: Company;
  company_descriptions: CompanyDescription[] | null;
  company_followings: Array<WtdModels.Partial<WtdModels.CompanyFollowing, "id" | "company_id">>;
  experimentings: Array<WtdModels.Partial<WtdModels.Experimenting, "experiment_name" | "variation">>;
}

export const requestToLoadCompanyAbout = requestCreator<number | string, CompanyAboutResponse, Error>(
  companyIdOrName => ({
    path: `/companies/${companyIdOrName}/about.json`,
  })
);

/**
 * GET /companies/:companyId/members
 */

export interface CompanyMembersResponseModels {
  CompanyMembershipSection: WtdModels.Partial<
    WtdModels.CompanyMembershipSection,
    "id" | "name" | "description" | "display_order"
  >;
  CompanyMembership: WtdModels.Partial<
    WtdModels.CompanyMembership,
    "id" | "display_order" | "featured_display_order" | "position",
    {
      section: WtdModels.Partial<WtdModels.CompanyMembershipSection, "id">;
      user: WtdModels.Partial<
        WtdModels.User,
        "id" | "user_path" | "name",
        {
          avatar: WtdModels.Partial<WtdModels.Image, "id" | "url">;
          profile: WtdModels.Partial<
            WtdModels.Profile,
            "id" | "introduction" | "position",
            {
              cover_post: WtdModels.Partial<
                WtdModels.Post,
                "id" | "post_path" | "title",
                {
                  image: WtdModels.Partial<WtdModels.Image, "id" | "url">;
                }
              >;
            }
          >;
        }
      >;
    }
  >;
}

export interface CompanyMembersResponse {
  company: Company;
  sections: Array<CompanyMembersResponseModels["CompanyMembershipSection"]>;
  memberships: Array<CompanyMembersResponseModels["CompanyMembership"]>;
  company_followings: Array<WtdModels.Partial<WtdModels.CompanyFollowing, "id" | "company_id">>;
  experimentings: Array<WtdModels.Partial<WtdModels.Experimenting, "experiment_name" | "variation">>;
}

export const requestToLoadCompanyMembers = requestCreator<number | string, CompanyMembersResponse, Error>(
  companyIdOrName => ({
    path: `/companies/${companyIdOrName}/members.json`,
  })
);

/**
 * GET /companies/:companyId/feed
 */

export type Post = WtdModels.Partial<
  WtdModels.Post,
  "id" | "post_path" | "title" | "description_head" | "published_at" | "published_at_from_now_in_words",
  {
    liked_status: WtdModels.Partial<WtdModels.LikedStatus, "state">;
    image: WtdModels.Partial<WtdModels.Image, "id" | "url">;
    company: WtdModels.Partial<
      WtdModels.Company,
      "id" | "company_path" | "name",
      {
        avatar: WtdModels.Partial<WtdModels.Image, "id" | "url">;
      }
    >;
    post_space: WtdModels.Partial<
      WtdModels.PostSpace,
      "id" | "post_space_path" | "title",
      {
        icon: WtdModels.Partial<WtdModels.Image, "id" | "url">;
      }
    >;
  }
>;

export type PostSpace = WtdModels.Partial<
  WtdModels.PostSpace,
  "id" | "post_space_path" | "title" | "space_name",
  {
    cover_image: WtdModels.Partial<WtdModels.Image, "id" | "url">;
  }
>;

export interface CompanyPostsResponse {
  company: Company;
  posts: Post[];
  post_pins: CompanyPostPin[];
  latest_pinnable_posts: PinnablePost[];
  spaces: PostSpace[];
  company_followings: Array<WtdModels.Partial<WtdModels.CompanyFollowing, "id" | "company_id">>;
  experimentings: Array<WtdModels.Partial<WtdModels.Experimenting, "experiment_name" | "variation">>;
}

export const requestToLoadCompanyPosts = requestCreator<number | string, CompanyPostsResponse, Error>(
  companyIdOrName => ({
    path: `/companies/${companyIdOrName}/feed.json`,
  })
);

/**
 * GET /companies/:companyId/projects
 */

export interface CompanyProjectsResponse {
  company: Company;
  projects: Array<
    WtdModels.Partial<
      WtdModels.Project,
      | "id"
      | "project_path"
      | "title"
      | "looking_for"
      | "occupation_type"
      | "localized_occupation_type"
      | "plain_description"
      | "published_at"
      | "published_at_from_now_in_words"
      | "support_count"
      | "supported"
      | "can_support"
      | "bookmarked",
      {
        image: WtdModels.Partial<WtdModels.Image, "id" | "url">;
      }
    >
  >;
  company_followings: Array<WtdModels.Partial<WtdModels.CompanyFollowing, "id" | "company_id">>;
  experimentings: Array<WtdModels.Partial<WtdModels.Experimenting, "experiment_name" | "variation">>;
}

export const requestToLoadCompanyProjects = requestCreator<number | string, CompanyProjectsResponse, Error>(
  companyIdOrName => ({
    path: `/companies/${companyIdOrName}/projects.json`,
  })
);

/**
 * GET /api/v2/companies/:companyId/similar_companies
 */

export type SimilarCompany = WtdModels.Partial<
  WtdModels.Company,
  "id" | "name" | "company_path" | "short_location" | "mission_statement",
  {
    avatar: Avatar;
    background: Background;
  }
>;

export type SimilarCompaniesResponse = SimilarCompany[];
export const requestToLoadSimilarCompanies = requestCreator<number, SimilarCompaniesResponse, Error>(companyId => {
  return {
    path: `/api/v2/companies/${companyId}/similar_companies`,
    query: {
      fields: [
        "name",
        "company_name",
        "short_location",
        "mission_statement",
        "company_path",
        "background.url",
        "avatar.url",
      ].join(","),
      include: ["avatar", "background"].join(","),
    },
  };
});

export type UpdateCompanyResponse = Company;

export const requestToUpdateCompany = requestCreator<
  { company: Company; avatar?: Company["avatar"]; background?: Company["background"] },
  UpdateCompanyResponse,
  UnknownError
>(({ company, avatar, background }) => {
  const { avatar: _avatar, background: _background, ...rest } = company;
  const body: any = rest;
  if (avatar) {
    body.avatar_attributes = { import_url: avatar.url };
  }
  if (background) {
    body.background_attributes = { import_url: background.url };
  }
  return {
    path: `/api/v2/companies/${company.id}`,
    method: "PUT",
    query: {
      fields: [
        "id",
        "name",
        "company_name",
        "company_path",
        "font_color_code",
        "short_location",
        "address_prefix",
        "address_suffix",
        "latitude",
        "longitude",
        "url",
        "founded_on",
        "founder",
        "origin",
        "mission_statement",
        "misc_tag_names",
        "payroll_number",
        "policy_update",
        "is_following",
        "project_count",
        "post_count",
        "lastest_post_published_at",
        "has_descriptions",
        "is_international_feed",
        "country",
        "twitter",
        "facebook",
        "avatar.id",
        "avatar.url",
        "background.id",
        "background.url",
      ].join(","),
      include: ["avatar", "background"].join(","),
    },
    body,
  };
});

export const requestToCreatePostPin = requestCreator<CompanyPostPin, CompanyPostPin, UnknownError>(pin => ({
  path: "/api/v2/company_post_pins",
  method: "POST",
  query: {
    fields: [
      "id",
      "category_cd",
      "company.id",
      "post.id",
      "post.post_path",
      "post.title",
      "post.image.id",
      "post.image.url",
    ].join(","),
    include: ["company", "post", "post.image"].join(","),
  },
  body: {
    company_id: pin.company.id,
    post_id: pin.post!.id,
    category_cd: pin.category_cd,
  },
}));

export const requestToDeletePostPin = requestCreator<number, null, UnknownError>(pinId => ({
  path: `/api/v2/company_post_pins/${pinId}`,
  method: "DELETE",
}));

/*
 * Update company values
 */
export type UpdateCompanyValuesResponse = CompanyValue[];

export const requestToUpdateCompanyValues = requestCreator<
  {
    companyId: number;
    companyValues: Array<WtdModels.Partial<WtdModels.CompanyValue, "feature" | "description" | "rank">>;
  },
  UpdateCompanyValuesResponse,
  Error
>(params => ({
  path: `/api/v2/companies/${params.companyId}/company_values`,
  method: "PUT",
  query: {
    fields: [
      "id",
      "feature",
      "feature_cd",
      "feature_color",
      "feature_title",
      "feature_title_with_break",
      "description",
      "rank",
    ].join(","),
    include: [].join(","),
  },
  body: {
    company_values: params.companyValues,
  },
}));

/*
 * POST /api/v2/companies/:companyId/posts
 */

export type LoadPostsResponse = Post[];

export const requestToLoadPosts = requestCreator<{ companyId: number; page: number }, LoadPostsResponse, Error>(
  params => ({
    path: `/api/v2/companies/${params.companyId}/posts`,
    query: {
      company_id: params.companyId,
      fields: [
        "id",
        "post_path",
        "title",
        "description_head",
        "published_at_from_now_in_words",
        "image.id",
        "image.url",
        "liked_status.state",
        "company.id",
        "company.company_path",
        "company.name",
        "company.avatar.id",
        "company.avatar.url",
        "post_space.id",
        "post_space.post_space_path",
        "post_space.title",
        "post_space.icon.id",
        "post_space.icon.url",
      ].join(","),
      include: ["image", "liked_status", "company", "company.avatar", "post_space", "post_space.icon"].join(","),
      page: params.page,
      per_page: 15,
      page_count: true,
      sort: "-published_at",
    },
    method: "GET",
  })
);

export type loadPinnablePostsResponse = PinnablePost[];

export const requestToLoadPinnablePosts = requestCreator<{ companyId: number }, loadPinnablePostsResponse, Error>(
  params => ({
    path: `/api/v2/posts/pinnable_posts`,
    query: {
      target: "company",
      target_id: params.companyId,
      fields: ["id", "post_path", "title", "image.id", "image.url"].join(","),
      include: "image",
      sort: "-published_at",
    },
  })
);

export type LoadInvisibleCompanyValuesResponse = any[];

export const requestToLoadInvisibleCompanyValues = requestCreator<
  { companyId: number },
  LoadInvisibleCompanyValuesResponse,
  Error
>(params => ({
  path: "/api/v2/company_values/invisibles",
  query: {
    company_id: params.companyId,
    fields: ["id", "feature_title", "feature_title_with_break", "feature", "description", "rank", "feature_color"].join(
      ","
    ),
  },
  method: "GET",
}));

/*
 * POST /api/v2/posts
 */

export interface CreateCompanyPostPayload {
  companyId: number;
  userId: number;
  description: string;
  imageUrl?: string;
}

interface CreateCompanyPostBody {
  company_id: number;
  user_id: number;
  description: string;
  category: WtdModels.PostCategory;
  state: WtdModels.PostState;
  internal: boolean;
  image_attributes?: {
    import_url: string;
  };
}

export const requestToSendCompanyPost = requestCreator<CreateCompanyPostPayload, Post, UnknownError>(params => {
  let body: CreateCompanyPostBody = {
    company_id: params.companyId,
    user_id: params.userId,
    description: params.description,
    category: "feed", // TODO: Enable to change category
    state: "published",
    internal: false,
  };

  if (typeof params.imageUrl !== "undefined") {
    body = { ...body, image_attributes: { import_url: params.imageUrl } };
  }

  return {
    path: "/api/v2/posts",
    query: {
      fields: [
        "id",
        "post_path",
        "title",
        "description_head",
        "published_at",
        "published_at_from_now_in_words",
        "liked_status.state",
        "image.id",
        "image.url",
        "company.id",
        "company.company_path",
        "company.name",
        "company.avatar.id",
        "company.avatar.url",
      ].join(","),
      include: ["liked_status", "image", "company", "company.avatar"].join(","),
    },
    method: "POST",
    body,
  };
});



// WEBPACK FOOTER //
// ./frontend/src/companies/api/index.ts